import { currencyInfo, isSupportedCurrency } from '@fd/money';
import type { InputHTMLAttributes } from 'react';
import { cashShown, cashText, decimalMark } from '../app/cash-entry.ts';
import { useT } from '../app/i18n.tsx';
import { useTheme } from '../app/theme.tsx';

type Props = Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'> & {
  value: string;
  onValue(text: string): void;
  /** Sets how many digits are pence (two unless the currency says otherwise). */
  currency?: string;
  /** Not money (a share in percent): always typed as usual. */
  plain?: boolean;
};

/**
 * A money field. Comma and dot both work as usual; with "Kwoty jak w kasie" on (Settings, this
 * device) the digits come in from the right instead, 12568 → 125,68, on the phone's digit pad.
 * Focusing such a field selects what it holds, so the next digits type a new amount.
 */
export function AmountInput({ value, onValue, currency, plain, inputMode, onFocus, ...rest }: Props) {
  const t = useT();
  const { cashEntry } = useTheme();
  const cash = cashEntry && !plain;
  const code = currency && isSupportedCurrency(currency) ? currency : 'GBP';
  const scale = currencyInfo(code).scale;
  return (
    <input
      {...rest}
      inputMode={cash ? 'numeric' : (inputMode ?? 'decimal')}
      value={value}
      onChange={(e) => onValue(cash ? cashText(e.target.value, scale, decimalMark(t.locale)) : e.target.value)}
      onFocus={(e) => {
        if (cash) {
          const shown = cashShown(value, code, scale, t.locale);
          if (shown !== value) onValue(shown);
          const input = e.currentTarget;
          requestAnimationFrame(() => input.select());
        }
        onFocus?.(e);
      }}
    />
  );
}
